import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import ProductForm from './components/ProductForm';
import ProductList from './components/ProductList';
import ProductStats from './components/ProductStats';
import StockMovements from './components/StockMovements';
import LowStockAlerts from './components/LowStockAlerts';

import Button from '../../components/ui/Button';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'stock'
  const [isEditing, setIsEditing] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);

  // Mock products data with enhanced fields
  useEffect(() => {
    const mockProducts = [
      {
        id: 1,
        sku: 'LAP-DELL-001',
        name: 'Laptop Dell XPS 13',
        category: 'Portátiles',
        brand: 'Dell',
        model: 'XPS 13 9320',
        description: 'Portátil ultradelgado con procesador Intel Core i7 de 12ª generación',
        price: 1299.99,
        cost: 950.00,
        currentStock: 15,
        minimumStock: 5,
        maximumStock: 50,
        reservedStock: 3,
        availableStock: 12,
        location: 'A1-01',
        status: 'active',
        supplier: {
          id: 1,
          name: 'Dell Technologies',
          contact: 'ventas@dell.es',
          leadTime: 7
        },
        specifications: {
          weight: '1.2 kg',
          dimensions: '295.7 x 198.1 x 14.8 mm',
          color: 'Plata',
          material: 'Aluminio'
        },
        images: [
          'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400'
        ],
        lastUpdated: new Date(Date.now() - 86400000),
        createdAt: new Date(Date.now() - 2592000000),
        tags: ['laptop', 'ultrabook', 'premium'],
        barcode: '1234567890123',
        warranty: 24
      },
      {
        id: 2,
        sku: 'PHN-IPH-002',
        name: 'iPhone 15 Pro',
        category: 'Smartphones',
        brand: 'Apple',
        model: 'iPhone 15 Pro',
        description: 'Smartphone premium con chip A17 Pro y cámara de 48MP',
        price: 1199.00,
        cost: 850.00,
        currentStock: 3,
        minimumStock: 10,
        maximumStock: 30,
        reservedStock: 1,
        availableStock: 2,
        location: 'B2-03',
        status: 'active',
        supplier: {
          id: 2,
          name: 'Apple España',
          contact: 'distribuidores@apple.es',
          leadTime: 14
        },
        specifications: {
          weight: '0.187 kg',
          dimensions: '146.6 x 70.6 x 8.25 mm',
          color: 'Titanio Natural',
          material: 'Titanio'
        },
        images: [
          'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400'
        ],
        lastUpdated: new Date(Date.now() - 172800000),
        createdAt: new Date(Date.now() - 1296000000),
        tags: ['smartphone', 'premium', 'apple'],
        barcode: '2345678901234',
        warranty: 12
      },
      {
        id: 3,
        sku: 'TAB-SAM-003',
        name: 'Samsung Galaxy Tab S9',
        category: 'Tablets',
        brand: 'Samsung',
        model: 'Galaxy Tab S9',
        description: 'Tablet premium con pantalla AMOLED de 11 pulgadas',
        price: 849.99,
        cost: 620.00,
        currentStock: 0,
        minimumStock: 5,
        maximumStock: 25,
        reservedStock: 0,
        availableStock: 0,
        location: 'C1-05',
        status: 'out_of_stock',
        supplier: {
          id: 3,
          name: 'Samsung Electronics',
          contact: 'b2b@samsung.es',
          leadTime: 10
        },
        specifications: {
          weight: '0.498 kg',
          dimensions: '254.3 x 165.8 x 5.9 mm',
          color: 'Grafito',
          material: 'Aluminio'
        },
        images: [
          'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400'
        ],
        lastUpdated: new Date(Date.now() - 259200000),
        createdAt: new Date(Date.now() - 1728000000),
        tags: ['tablet', 'samsung', 'amoled'],
        barcode: '3456789012345',
        warranty: 24
      }
    ];

    setProducts(mockProducts);
  }, []);

  // Mock stock movements
  useEffect(() => {
    const mockMovements = [
      {
        id: 1,
        productId: 1,
        productName: 'Laptop Dell XPS 13',
        type: 'adjustment',
        quantity: -2,
        reason: 'inventory_count',
        employeeName: 'Carlos Martínez',
        timestamp: new Date(Date.now() - 300000),
        notes: 'Ajuste por recuento físico'
      },
      {
        id: 2,
        productId: 2,
        type: 'sale',
        quantity: -1,
        reason: 'sale',
        employeeName: 'Ana García',
        timestamp: new Date(Date.now() - 900000),
        notes: 'Venta directa'
      },
      {
        id: 3,
        productId: 1,
        type: 'purchase',
        quantity: 10,
        reason: 'supplier_delivery',
        employeeName: 'Luis Rodríguez',
        timestamp: new Date(Date.now() - 1800000),
        notes: 'Recepción de proveedor'
      }
    ];

    setStockMovements(mockMovements);
  }, []);

  const handleCreateNew = () => {
    setSelectedProduct(null);
    setCurrentView('form');
    setIsEditing(false);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setCurrentView('form');
    setIsEditing(true);
  };

  const handleSave = (productData) => {
    if (isEditing && selectedProduct) {
      // Update existing product
      setProducts(prev => 
        prev.map(p => 
          p.id === selectedProduct.id 
            ? { ...productData, id: selectedProduct.id, lastUpdated: new Date() }
            : p
        )
      );
    } else {
      // Create new product
      const newProduct = {
        ...productData,
        id: Date.now(),
        createdAt: new Date(),
        lastUpdated: new Date(),
        currentStock: 0,
        reservedStock: 0,
        availableStock: 0,
        status: 'active'
      };
      setProducts(prev => [newProduct, ...prev]);
    }
    
    setCurrentView('list');
    setSelectedProduct(null);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setCurrentView('list');
    setSelectedProduct(null);
    setIsEditing(false);
  };

  const handleStockAdjustment = (productId, quantity, reason, notes) => {
    // Update product stock
    setProducts(prev => 
      prev.map(p => {
        if (p.id === productId) {
          const newStock = Math.max(0, p.currentStock + quantity);
          return {
            ...p,
            currentStock: newStock,
            availableStock: newStock - p.reservedStock,
            lastUpdated: new Date(),
            status: newStock === 0 ? 'out_of_stock' : newStock <= p.minimumStock ? 'low_stock' : 'active'
          };
        }
        return p;
      })
    );

    // Add stock movement record
    const newMovement = {
      id: Date.now(),
      productId: productId,
      productName: products.find(p => p.id === productId)?.name,
      type: quantity > 0 ? 'adjustment_in' : 'adjustment_out',
      quantity: quantity,
      reason: reason,
      employeeName: 'Usuario Actual', // In real app, get from auth context
      timestamp: new Date(),
      notes: notes
    };

    setStockMovements(prev => [newMovement, ...prev]);
  };

  const handleProductDelete = (productId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este producto?')) {
      setProducts(prev => prev.filter(p => p.id !== productId));
      
      if (selectedProduct?.id === productId) {
        setSelectedProduct(null);
        setCurrentView('list');
      }
    }
  };

  const handleProductStatusChange = (productId, newStatus) => {
    setProducts(prev => 
      prev.map(p => 
        p.id === productId 
          ? { ...p, status: newStatus, lastUpdated: new Date() }
          : p
      )
    );
  };

  const getLowStockProducts = () => {
    return products.filter(p => 
      p.status === 'active' && 
      p.currentStock <= p.minimumStock && 
      p.currentStock > 0
    );
  };

  const getOutOfStockProducts = () => {
    return products.filter(p => p.currentStock === 0);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation />
          
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">
                  Gestión de Productos
                </h1>
                <p className="text-text-secondary">
                  Alta, baja y modificación de productos con control de stock
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setCurrentView('stock')}
                  iconName="BarChart3"
                >
                  Ver Movimientos
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateNew}
                  iconName="Plus"
                >
                  Nuevo Producto
                </Button>
              </div>
            </div>
          </div>

          <ProductStats products={products} />

          {currentView === 'form' ? (
            <ProductForm
              product={selectedProduct}
              onSave={handleSave}
              onCancel={handleCancel}
              isEditing={isEditing}
            />
          ) : currentView === 'stock' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <StockMovements 
                movements={stockMovements}
                onBack={() => setCurrentView('list')}
              />
              <LowStockAlerts
                lowStockProducts={getLowStockProducts()}
                outOfStockProducts={getOutOfStockProducts()}
                onAdjustStock={handleStockAdjustment}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              <div className="xl:col-span-3">
                <ProductList
                  products={products}
                  onEdit={handleEdit}
                  onDelete={handleProductDelete}
                  onStatusChange={handleProductStatusChange}
                  onStockAdjustment={handleStockAdjustment}
                />
              </div>
              
              <div className="xl:col-span-1">
                <LowStockAlerts
                  lowStockProducts={getLowStockProducts()}
                  outOfStockProducts={getOutOfStockProducts()}
                  onAdjustStock={handleStockAdjustment}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <QuickActionFAB />
    </div>
  );
};

export default ProductManagement;